import {
  AppLayoutMode,
  IPaymentMethodReqUI,
  ISharedInvoicePaymentUI,
  ITenant,
  PaymentMethodReqSchemaUI,
  SharedInvoicePaymentSchemaUI,
  ToastItem,
} from '@monetize/types/app';
import { FilterType, TDataTablePager } from '@monetize/types/common';
import { TIMEZONE_CONFIG } from '@monetize/utils/core';
import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import type { SetStateAction } from 'react';
import * as z from 'zod';
import { atomWithPersistence } from './atomWithPersistence';
import { resolveSetStateAction } from './setStateAction';
import {
  ContractsViewMode,
  ContractsViewModeSchema,
  getDefaultFilter,
  getDefaultPager,
  PersistKeysEnum,
  numberValueSchema,
  QuoteLocalPreferences,
  quoteLocalPreferencesSchema,
  RedirectInfo,
  RedirectInfoSchema,
  SharedAccountPaymentMethodStripeCallbackSchema,
  SharedAccountPaymentMethodStripeCallbackTemp,
  SharedQuotePaymentMethodStripeCallbackSchema,
  SharedQuotePaymentMethodStripeCallbackTemp,
  SortSchema,
  SortType,
  StorageOptions,
  stringValueSchema,
  TableFilterSchema,
  TablePagerSchema,
  TableSearchTermSchema,
  TenantStorageSchema,
} from './store.types';
import {
  clearBrowserStorage,
  getFromBrowserStorage,
  getLastTenantId,
  removeFalseValuesFromAdvancedQuoteOptions,
  saveLastTenantId,
  saveToBrowserStorage,
} from './store.utils';
import { CHAT_PANEL_DEFAULT_WIDTH } from '../constants/chat';

/**
 *
 * *****GLOBAL STORE*****
 *
 * The global store is used to store data that is used across the entire application
 * or data that should be persisted to localstorage
 *
 * The store is implemented using Jotai {@link https://jotai.org/}
 *
 * global.store -> This includes all atoms and derived atoms
 * store.types -> This includes types BUT also includes the default table filters and pagers used across the application
 * store.utils -> This includes all helper functions used in the store, mostly used for storage mechanisms
 * appStore -> The store instance behind the Provider, for reading/writing atoms outside React
 *
 * **** Storing data in localstorage or sessionStorage ****
 * * You probable want to set the default from existing values, so use `getFromBrowserStorage` on the default
 * * Use `atomWithPersistence` and save values with the `saveToBrowserStorage` function in its persist callback
 * * For more complex use-cases, like storing a lot of values by key
 *    then look at these examples: tableFiltersState, tableFilterSelector, tablePagerState, tablePagerSelector
 *    And look at components using tableFilterSelector and tablePagerSelector
 * * You will need to add ZOD schemas to validate incoming and outgoing data and add the schema to SchemaValidators
 *    this ensures that we never have data in an unexpected shape coming into the application
 *
 */

// This stores the function provided from Auth0 useAuth hook so that it can be accessed in Axios interceptor
export const refreshAccessTokenState = atom<(() => Promise<string>) | null>(
  null,
);

export const currentUserIdState = atomWithPersistence<string>(
  getFromBrowserStorage({
    persistKey: PersistKeysEnum.USER_ID,
    schemaValidator: stringValueSchema,
  }),
  // Playwright relies on this
  (newValue) => {
    if (newValue) {
      saveToBrowserStorage({
        persistKey: PersistKeysEnum.USER_ID,
        schemaValidator: stringValueSchema,
        value: newValue as any,
      });
    } else {
      clearBrowserStorage({ persistKey: PersistKeysEnum.USER_ID });
    }
  },
);

const DEFAULT_TENANT_STATE = getFromBrowserStorage({
  persistKey: PersistKeysEnum.CURRENT_TENANT,
  schemaValidator: TenantStorageSchema,
  defaultValue: null,
  type: StorageOptions.ALL,
});

// Initialize default timezone
if (DEFAULT_TENANT_STATE) {
  TIMEZONE_CONFIG.tenantTimezone = DEFAULT_TENANT_STATE.timezone;
  // The last-tenant key is otherwise only written when the tenant changes
  // (persist callback), so a session that already had a tenant selected before
  // the key existed would lose it on its first logout. Seed it from the stored
  // tenant.
  if (DEFAULT_TENANT_STATE.id && !getLastTenantId()) {
    saveLastTenantId(DEFAULT_TENANT_STATE.id);
  }
}

export const currentTenantState = atomWithPersistence<ITenant | null>(
  DEFAULT_TENANT_STATE,
  (newValue) => {
    if (newValue) {
      saveToBrowserStorage({
        persistKey: PersistKeysEnum.CURRENT_TENANT,
        schemaValidator: TenantStorageSchema,
        value: newValue as any,
        type: StorageOptions.ALL,
      });
      if (newValue.id) {
        saveLastTenantId(newValue.id);
      }
      TIMEZONE_CONFIG.tenantTimezone = newValue.timezone;
    } else {
      clearBrowserStorage({
        persistKey: PersistKeysEnum.CURRENT_TENANT,
        type: StorageOptions.ALL,
      });
      TIMEZONE_CONFIG.tenantTimezone = TIMEZONE_CONFIG.userTimezone;
    }
  },
);

export const accessTokenState = atomWithPersistence<string>(
  getFromBrowserStorage({
    persistKey: PersistKeysEnum.ACCESS_TOKEN,
    schemaValidator: stringValueSchema,
  }),
  (newValue) =>
    saveToBrowserStorage({
      persistKey: PersistKeysEnum.ACCESS_TOKEN,
      schemaValidator: stringValueSchema,
      value: newValue,
    }),
);

export const activeToastState = atom<ToastItem | null>(null);

export const switchTenantVisibleState = atom<boolean>(false);

export const navigationState = atom<{
  route: string;
  config?: any;
} | null>(null);

// Set as soon as a logout is initiated so in-flight/background token refreshes
// (silent auth query, axios auto-refresh) know to stop instead of re-authenticating
export const isLoggingOutState = atom<boolean>(false);

export const quoteLocalPreferencesState =
  atomWithPersistence<QuoteLocalPreferences>(
    getFromBrowserStorage({
      persistKey: PersistKeysEnum.QUOTE_PREFERENCES,
      schemaValidator: quoteLocalPreferencesSchema,
      defaultValue: {},
    }),
    (newValue) =>
      saveToBrowserStorage({
        persistKey: PersistKeysEnum.QUOTE_PREFERENCES,
        schemaValidator: quoteLocalPreferencesSchema,
        value: newValue,
        transformFn: removeFalseValuesFromAdvancedQuoteOptions,
      }),
  );

export const tempPaymentMethodState =
  atomWithPersistence<IPaymentMethodReqUI | null>(
    getFromBrowserStorage({
      persistKey: PersistKeysEnum.PAYMENT_METHOD,
      schemaValidator: PaymentMethodReqSchemaUI,
      defaultValue: null,
    }),
    (newValue) => {
      if (newValue) {
        saveToBrowserStorage({
          persistKey: PersistKeysEnum.PAYMENT_METHOD,
          schemaValidator: PaymentMethodReqSchemaUI,
          value: newValue as any,
        });
      } else {
        clearBrowserStorage({ persistKey: PersistKeysEnum.PAYMENT_METHOD });
      }
    },
  );

export const tempSharedInvoicePaymentState =
  atomWithPersistence<ISharedInvoicePaymentUI | null>(
    getFromBrowserStorage({
      persistKey: PersistKeysEnum.SHARED_INVOICE_KEY,
      schemaValidator: SharedInvoicePaymentSchemaUI,
      defaultValue: null,
    }),
    (newValue) => {
      if (newValue) {
        saveToBrowserStorage({
          persistKey: PersistKeysEnum.SHARED_INVOICE_KEY,
          schemaValidator: SharedInvoicePaymentSchemaUI,
          value: newValue as any,
        });
      } else {
        clearBrowserStorage({
          persistKey: PersistKeysEnum.SHARED_INVOICE_KEY,
        });
      }
    },
  );

export const tempSharedQuotePaymentMethodState =
  atomWithPersistence<SharedQuotePaymentMethodStripeCallbackTemp | null>(
    getFromBrowserStorage({
      persistKey:
        PersistKeysEnum.SHARED_QUOTE_PAYMENT_METHOD_STRIPE_CALLBACK_KEY,
      schemaValidator: SharedQuotePaymentMethodStripeCallbackSchema,
      defaultValue: null,
    }),
    (newValue) => {
      if (newValue) {
        saveToBrowserStorage({
          persistKey:
            PersistKeysEnum.SHARED_QUOTE_PAYMENT_METHOD_STRIPE_CALLBACK_KEY,
          schemaValidator: SharedQuotePaymentMethodStripeCallbackSchema,
          value: newValue as any,
        });
      } else {
        clearBrowserStorage({
          persistKey:
            PersistKeysEnum.SHARED_QUOTE_PAYMENT_METHOD_STRIPE_CALLBACK_KEY,
        });
      }
    },
  );

export const tempSharedAccountPaymentMethodState =
  atomWithPersistence<SharedAccountPaymentMethodStripeCallbackTemp | null>(
    getFromBrowserStorage({
      persistKey:
        PersistKeysEnum.SHARED_ACCOUNT_PAYMENT_METHOD_STRIPE_CALLBACK_KEY,
      schemaValidator: SharedAccountPaymentMethodStripeCallbackSchema,
      defaultValue: null,
    }),
    (newValue) => {
      if (newValue) {
        saveToBrowserStorage({
          persistKey:
            PersistKeysEnum.SHARED_ACCOUNT_PAYMENT_METHOD_STRIPE_CALLBACK_KEY,
          schemaValidator: SharedAccountPaymentMethodStripeCallbackSchema,
          value: newValue as any,
        });
      } else {
        clearBrowserStorage({
          persistKey:
            PersistKeysEnum.SHARED_ACCOUNT_PAYMENT_METHOD_STRIPE_CALLBACK_KEY,
        });
      }
    },
  );

export const tempRedirectInfoState = atomWithPersistence<RedirectInfo | null>(
  getFromBrowserStorage({
    persistKey: PersistKeysEnum.REDIRECT_INFO,
    schemaValidator: RedirectInfoSchema,
    defaultValue: null,
  }),
  (newValue) => {
    if (newValue) {
      saveToBrowserStorage({
        persistKey: PersistKeysEnum.REDIRECT_INFO,
        schemaValidator: RedirectInfoSchema,
        value: newValue as any,
      });
    } else {
      clearBrowserStorage({ persistKey: PersistKeysEnum.REDIRECT_INFO });
    }
  },
);

export const offeringRateFormSortValueState = atomWithPersistence<SortType>(
  getFromBrowserStorage({
    persistKey: PersistKeysEnum.OFFERING_RATE_FORM_SORT_VALUE,
    schemaValidator: SortSchema,
    defaultValue: { value: 'name', order: 'asc' },
  }),
  (newValue) =>
    saveToBrowserStorage({
      persistKey: PersistKeysEnum.OFFERING_RATE_FORM_SORT_VALUE,
      schemaValidator: SortSchema,
      value: newValue,
    }),
);

export const contractsViewModeState = atomWithPersistence<ContractsViewMode>(
  getFromBrowserStorage({
    persistKey: PersistKeysEnum.CONTRACTS_VIEW_MODE,
    schemaValidator: ContractsViewModeSchema,
    defaultValue: 'cards',
  }),
  (newValue) =>
    saveToBrowserStorage({
      persistKey: PersistKeysEnum.CONTRACTS_VIEW_MODE,
      schemaValidator: ContractsViewModeSchema,
      value: newValue,
    }),
);

/**
 * This filter includes the entire store
 * use the tableFilterSelector family to get a slice
 */
export const tableFiltersState = atomWithPersistence(
  getFromBrowserStorage({
    persistKey: PersistKeysEnum.TABLE_FILTERS,
    schemaValidator: TableFilterSchema,
    defaultValue: {} as Record<string, FilterType[]>,
    type: StorageOptions.SESSION,
  }),
  (newValue) => {
    if (newValue) {
      saveToBrowserStorage({
        persistKey: PersistKeysEnum.TABLE_FILTERS,
        schemaValidator: TableFilterSchema,
        value: newValue,
        type: StorageOptions.SESSION,
      });
    }
  },
);

export const tableFilterSelector = atomFamily((filterKey: string) =>
  atom(
    (get) =>
      (get(tableFiltersState)[filterKey] ||
        getDefaultFilter(filterKey)) as FilterType[],
    (get, set, update: SetStateAction<FilterType[]>) => {
      const filters = get(tableFiltersState);
      set(tableFiltersState, {
        ...filters,
        [filterKey]: resolveSetStateAction(
          update,
          (filters[filterKey] || getDefaultFilter(filterKey)) as FilterType[],
        ),
      });
    },
  ),
);

export const tablePagerState = atomWithPersistence(
  getFromBrowserStorage({
    persistKey: PersistKeysEnum.TABLE_PAGERS,
    schemaValidator: TablePagerSchema,
    defaultValue: {} as Record<string, TDataTablePager>,
    type: StorageOptions.SESSION,
  }),
  (newValue) => {
    if (newValue) {
      saveToBrowserStorage({
        persistKey: PersistKeysEnum.TABLE_PAGERS,
        schemaValidator: TablePagerSchema,
        value: newValue,
        type: StorageOptions.SESSION,
      });
    }
  },
);

export const tablePagerSelector = atomFamily((filterKey: string) =>
  atom(
    (get) =>
      (get(tablePagerState)[filterKey] ||
        getDefaultPager(filterKey)) as TDataTablePager,
    (get, set, update: SetStateAction<TDataTablePager>) => {
      const pagers = get(tablePagerState);
      set(tablePagerState, {
        ...pagers,
        [filterKey]: resolveSetStateAction(
          update,
          (pagers[filterKey] || getDefaultPager(filterKey)) as TDataTablePager,
        ),
      });
    },
  ),
);

export const tableSearchTermsState = atomWithPersistence(
  getFromBrowserStorage({
    persistKey: PersistKeysEnum.TABLE_SEARCH_TERMS,
    schemaValidator: TableSearchTermSchema,
    defaultValue: {} as Record<string, string>,
    type: StorageOptions.SESSION,
  }),
  (newValue) => {
    if (newValue) {
      saveToBrowserStorage({
        persistKey: PersistKeysEnum.TABLE_SEARCH_TERMS,
        schemaValidator: TableSearchTermSchema,
        value: newValue,
        type: StorageOptions.SESSION,
      });
    }
  },
);

export const tableSearchTermSelector = atomFamily((filterKey: string) =>
  atom(
    (get) => get(tableSearchTermsState)[filterKey] || '',
    (get, set, update: SetStateAction<string>) => {
      const searchTerms = get(tableSearchTermsState);
      set(tableSearchTermsState, {
        ...searchTerms,
        [filterKey]: resolveSetStateAction(
          update,
          searchTerms[filterKey] || '',
        ),
      });
    },
  ),
);

/**
 * This keeps track of the page layout state
 * Keeping here since we probably need to access it in the child component
 */
export const appLayoutState = atom<AppLayoutMode>('overlay');

export const chatPanelOpenState = atom<boolean>(false);

export const chatPanelWidthState = atomWithPersistence<number>(
  getFromBrowserStorage({
    persistKey: PersistKeysEnum.CHAT_PANEL_WIDTH,
    schemaValidator: numberValueSchema,
    defaultValue: CHAT_PANEL_DEFAULT_WIDTH,
    type: StorageOptions.LOCAL,
  }),
  (newValue) =>
    saveToBrowserStorage({
      persistKey: PersistKeysEnum.CHAT_PANEL_WIDTH,
      schemaValidator: numberValueSchema,
      value: newValue,
      type: StorageOptions.LOCAL,
    }),
);

export const appGlobalDataState = atom({
  hasMultipleCurrency: false,
  hasMultipleLegalEntity: false,
  hasCrmConfigured: false,
  hasHubspotConfigured: false,
});

export const currencyFilterState = atomWithPersistence<string>(
  getFromBrowserStorage({
    persistKey: PersistKeysEnum.DASHBOARD_CURRENCY_FILTER,
    schemaValidator: z.string(),
    type: StorageOptions.SESSION,
  }),
  (newValue) => {
    if (newValue) {
      saveToBrowserStorage({
        persistKey: PersistKeysEnum.DASHBOARD_CURRENCY_FILTER,
        schemaValidator: z.string(),
        value: newValue,
        type: StorageOptions.SESSION,
      });
    }
  },
);
